/**
 * SchedulerConnectionDiagram — an interactive teaching diagram.
 *
 * Shows an admin the two hops that connect an on-premises Scheduler to the estate:
 *   Step 1  a REGISTRATION token, generated in the Orchestrator, carried by hand to the
 *           scheduler. Once per scheduler.
 *   Step 2  a VAULT CONNECTION token, generated per vault, carried the same way. Once per vault.
 *
 * It is a teaching aid. It performs nothing, calls nothing, and knows nothing about the user's
 * real estate — every scheduler, vault and token on screen is illustrative.
 *
 * ---------------------------------------------------------------------------------------------
 * THE ONE THING THIS DIAGRAM EXISTS TO SAY
 *
 * There is deliberately NO line between the Orchestrator and the Scheduler. They never hold a
 * network connection; the token crosses that gap by clipboard or email, in a human's hands. Every
 * other pair of nodes on screen is joined by a drawn route. The absence is the lesson — do not
 * "fix" it by adding a connector.
 *
 * ---------------------------------------------------------------------------------------------
 * COLOUR IS NOT A CHANNEL HERE
 *
 * The two token types are told apart by SHAPE (pill vs radius-12 rectangle), GLYPH (key vs tag)
 * and a spelled-out LABEL. That is not belt-and-braces. Measured, the registration tone and the
 * vault tone differ by 1.01-1.03:1 in luminance in every theme — desaturate the diagram and they
 * are the same grey. The derivation and the full ratio table live at the top of
 * src/styles/diagram.css. Scheduler state is likewise carried by its sub-label text
 * ("not registered" / "registered · active"), not by tone alone.
 *
 * ---------------------------------------------------------------------------------------------
 * HOW IT IS BUILT
 *
 * A fixed design canvas, absolutely positioned, scaled to the container by a single
 * `transform: scale()`. Two canvases exist: a 1920x1080 wide layout, and an 800x1920 vertical
 * layout used below ~720px of container width. Same clock, same choreography, same copy at every
 * beat — only the coordinates differ.
 *
 * DOM, not SVG, for everything carrying text. The pills, chips and the caption box all size to
 * their content, which they must: German runs ~30% longer than English and nothing may be sized
 * to a specific string. SVG cannot measure text, so an SVG build would have needed hardcoded
 * widths per locale. SVG IS used for the scheduler-to-vault routes, which are pure geometry.
 *
 * Every visual is a pure function of the clock T. The engine (useDiagramClock) only advances,
 * holds and loops a number — there is no per-element animation state anywhere.
 *
 * ---------------------------------------------------------------------------------------------
 * TIMING
 *
 * One clock, 51s, six scenes, twelve step points. Every constant below is an absolute second on
 * that clock. Scene cues: Opening 0, Register 3, Choose 16, SaveVault 30, Repeat 34, Recap 46.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDiagramClock } from '@/hooks/useDiagramClock'
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion'
import '@/styles/diagram.css'

/* ============================================================================================ *
 * Clock constants
 * ============================================================================================ */

const DURATION = 51

/** Scene cues, in seconds. */
const REG = 3
const CHO = 16
const SAV = 30
const REP = 34
const RCP = 46

/**
 * The twelve step points. Each is a settled tableau — no half-drawn route, no mid-fade chip:
 * start · scheduler created · registration token on display · token pasted · registered+active ·
 * on Vaults · vault token on display · pasted at Add vault · vault 1 connected · vault 2
 * connected · vault 3 connected · recap.
 */
const BEATS = [0, 5.4, 9.4, 12.8, 15.4, 18.2, 25.2, 28.6, 32.6, 40.6, 46.5, 49.8] as const

/** The frozen tableau the diagram mounts into: the recap, complete, before the global fade. */
const RECAP_T = BEATS[11]

/** Caption changes. Each runs until the next one's time; the last carries an explicit end. */
const CAPTIONS: { at: number; key: string; until?: number }[] = [
    { at: 0, key: 'caption.open' },
    { at: REG + 0.3, key: 'caption.reg.create' },
    { at: REG + 2.5, key: 'caption.reg.generate' },
    { at: REG + 6.9, key: 'caption.reg.copy' },
    { at: REG + 8.7, key: 'caption.reg.paste' },
    { at: REG + 11.2, key: 'caption.reg.active' },
    { at: CHO + 0.3, key: 'caption.vault.gate' },
    { at: CHO + 2.3, key: 'caption.vault.setup' },
    { at: CHO + 5.5, key: 'caption.vault.generate' },
    { at: CHO + 9.9, key: 'caption.vault.copy' },
    { at: CHO + 11.7, key: 'caption.vault.paste' },
    { at: SAV + 1.4, key: 'caption.vault.save' },
    { at: REP + 0.3, key: 'caption.repeat' },
    { at: REP + 6.0, key: 'caption.repeat.done' },
    { at: RCP + 0.3, key: 'caption.recap' },
    { at: RCP + 2.6, key: 'caption.recap.once', until: RCP + 5.0 },
]
const CAPTION_FADE = 0.18

/**
 * Token payloads. Illustrative base64-ish strings, NEVER translated in any locale, and
 * deliberately meaningless — they decode to {"sched":"sched-01"} and {"vault":"eu-1"} style
 * placeholders. No real token, secret or credential belongs anywhere near this file.
 */
const PAYLOAD_REG = 'eyJzY2hlZCI6InNjaGVkLTAxIn0'
const PAYLOAD_VAULT = ['eyJ2YXVsdCI6ImV1LTEifQ', 'eyJ2YXVsdCI6InVzLTEifQ', 'eyJ2YXVsdCI6ImFwLTEifQ']

/** Ambiguous glyphs (I, O, l) removed so a scrambling payload never reads as a typo. */
const CHARSET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz0123456789+/'

/* ============================================================================================ *
 * Pure maths
 * ============================================================================================ */

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))
const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1)
const easeOutBack = (t: number) => {
    const c1 = 1.70158
    const c3 = c1 + 1
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2)
}

/** from -> to across [start, end], held at both ends. */
const tween = (T: number, from: number, to: number, start: number, end: number, ease: (t: number) => number) => {
    if (T <= start) return from
    if (T >= end) return to
    return from + (to - from) * ease((T - start) / (end - start))
}

/**
 * Deterministic character settle. Characters below `keep` show the real payload; the rest show a
 * hash of (index, frame), so seeking to any T reproduces exactly the same scramble — a scrubbed
 * timeline must not shimmer differently on every pass.
 */
function scrambleText(target: string, settled: number, frame: number): string {
    const n = target.length
    const keep = Math.floor(settled * n)
    let out = ''
    for (let i = 0; i < n; i++) {
        if (i < keep) {
            out += target[i]
            continue
        }
        const h = (((i + 1) * 73856093) ^ ((frame + i) * 19349663)) >>> 0
        out += CHARSET[h % CHARSET.length]
    }
    return out
}

/* ============================================================================================ *
 * Layout — two canvases, same choreography
 * ============================================================================================ */

interface NodeGeom {
    x: number
    y: number
    s: number
}
interface Pt {
    x: number
    y: number
}

interface Layout {
    id: 'wide' | 'narrow'
    W: number
    H: number
    /** The whole diagram group is offset so its visual centre lands at canvas centre. */
    offsetX: number
    offsetY: number
    O: NodeGeom
    S: NodeGeom
    V: NodeGeom[]
    ghost: NodeGeom
    /** Where a token materialises, and where it is pasted. */
    origin: Pt
    dest: Pt
    /** Clipboard position along the carry, c in [0,1]. */
    carry: (c: number) => Pt
    /** Action pill top edge = node.y + actionDY. */
    actionDY: number
    /** Scheduler confirmation top edge = S.y + confirmSchedDY. */
    confirmSchedDY: number
    vaultConfirm: (i: number) => Pt
    gate: Pt
    count: Pt
    chip1: Pt
    chip2: Pt
    routeStart: Pt
    routeEnd: (i: number) => Pt
    heading: { x: number; y: number; title: number; phase: number }
    caption: { x: number; y: number; maxW: number; size: number }
    tokenAxis: 'row' | 'column'
    /** Node label block below each card. */
    label: { size: number; sub: number; gap: number }
    hotspotExtra: number
    /** Font/geometry scale for the small furniture (pills, chips, tokens). */
    k: number
}

/**
 * WIDE — the 1920x1080 design canvas. The group sits 130px left of centre, which is where the
 * prototype put it: the diagram's visual mass is right of its geometric centre because the vault
 * fan hangs off the Scheduler.
 */
const WIDE: Layout = {
    id: 'wide',
    W: 1920,
    H: 1080,
    offsetX: 1920 / 2 - 1090,
    offsetY: 0,
    O: { x: 560, y: 430, s: 224 },
    S: { x: 1360, y: 430, s: 224 },
    V: [
        { x: 660, y: 840, s: 176 },
        { x: 1120, y: 840, s: 176 },
        { x: 1580, y: 840, s: 176 },
    ],
    ghost: { x: 1680, y: 395, s: 124 },
    origin: { x: 880, y: 330 },
    dest: { x: 1400, y: 330 },
    carry: (c) => ({
        x: 880 + 520 * easeInOutCubic(c),
        y: 330 + 96 - Math.sin(c * Math.PI) * 78,
    }),
    actionDY: 236,
    confirmSchedDY: 240,
    vaultConfirm: (i) => ({ x: [660, 1120, 1580][i], y: 840 - 136 }),
    gate: { x: 1360, y: 430 - 250 },
    count: { x: 1240, y: 80 },
    chip1: { x: 960, y: 370 },
    /**
     * The prototype puts chip 2 at (1360, 640), "below the Scheduler on the fan". It does not
     * fit there. The chip is ~100px tall; the band between the Scheduler's sub-label (ends 625)
     * and the vault confirmation row (starts 704) is 79px, and the vault confirmations have no
     * fade-out term, so all three are still on screen during the recap — which is the tableau
     * this diagram RESTS on. At 640 the sub "once per vault" rendered underneath the "Vault 3
     * connected · route open" pill; raising it to 596 only traded that for the "②" disc landing
     * on "registered · active".
     *
     * Moved below the vault row instead. It still labels the fan — it now sits directly beneath
     * the three vaults the words "once per vault" are about — and nothing overlaps it.
     */
    chip2: { x: 1360, y: 950 },
    routeStart: { x: 1360, y: 430 + 112 },
    routeEnd: (i) => ({ x: [660, 1120, 1580][i], y: 840 - 88 - 8 }),
    heading: { x: 96, y: 60, title: 46, phase: 22 },
    caption: { x: 96, y: 176, maxW: 780, size: 24 },
    tokenAxis: 'row',
    label: { size: 25, sub: 15, gap: 34 },
    hotspotExtra: 96,
    k: 1,
}

/**
 * NARROW — the vertical fallback below ~720px of container width.
 *
 * Same sequence read top to bottom: Orchestrator, the token hop, Scheduler, the fan, the three
 * vaults. Two things necessarily differ from the wide canvas, and both are readability, not
 * preference:
 *
 *   - The token lays out as a column. Scaling the wide token's row layout down to fit 800px put
 *     the payload chip at roughly 8px on screen, which is not a payload anyone can read.
 *   - The three vault confirmations stack below the vault row instead of sitting above each
 *     card. Side by side they are ~330px each against a 225px column pitch, so they overlapped
 *     each other and the "Add vault" pill.
 */
const NARROW: Layout = {
    id: 'narrow',
    W: 800,
    H: 1990,
    offsetX: 0,
    offsetY: 0,
    O: { x: 400, y: 440, s: 168 },
    S: { x: 400, y: 1204, s: 168 },
    V: [
        { x: 175, y: 1640, s: 120 },
        { x: 400, y: 1640, s: 120 },
        { x: 625, y: 1640, s: 120 },
    ],
    ghost: { x: 660, y: 1204, s: 100 },
    origin: { x: 400, y: 744 },
    dest: { x: 400, y: 994 },
    carry: (c) => ({
        x: 400 + Math.sin(c * Math.PI) * 160,
        y: 744 + 250 * easeInOutCubic(c),
    }),
    actionDY: 176,
    confirmSchedDY: 180,
    vaultConfirm: (i) => ({ x: 400, y: 1780 + i * 58 }),
    gate: { x: 400, y: 1068 },
    count: { x: 400, y: 268 },
    chip1: { x: 400, y: 800 },
    chip2: { x: 640, y: 1420 },
    /**
     * The routes start BELOW the Scheduler's label block, not at the card's bottom edge.
     *
     * In the wide canvas no route runs vertically — the middle vault sits left of the Scheduler,
     * so all three lines leave at an angle and clear the label by a few pixels. Stacked
     * vertically, vault 2 is directly beneath the Scheduler, so a route drawn from the card edge
     * runs straight down through "Scheduler" and "registered · active". Starting the fan below
     * the label reads as the label belonging to the card and the fan beginning after it.
     */
    routeStart: { x: 400, y: 1372 },
    routeEnd: (i) => ({ x: [175, 400, 625][i], y: 1640 - 60 - 8 }),
    heading: { x: 48, y: 40, title: 34, phase: 18 },
    caption: { x: 48, y: 124, maxW: 704, size: 20 },
    tokenAxis: 'column',
    label: { size: 20, sub: 13, gap: 24 },
    hotspotExtra: 76,
    k: 0.86,
}

/** Below this container width the wide canvas scales to an unreadable size; swap layouts. */
const NARROW_BREAKPOINT = 720

/* ============================================================================================ *
 * Choreography — every element's opacity, scale and position as a function of T
 * ============================================================================================ */

interface Hop {
    genOp: number
    genS: number
    settle: number
    clipOp: number
    clipS: number
    carry: number
    pasteOp: number
    pasteS: number
    ping: number
    pinging: boolean
    done: number
}

/**
 * One generate -> copy -> carry -> paste -> ping unit, used four times: once for the
 * registration token and once per vault. `sp` time-scales the whole unit (the two repeat vaults
 * run at 0.55x, the same sequence told faster because the viewer has already seen it).
 */
function hopSeq(T: number, t0: number, sp: number): Hop {
    const at = (o: number) => t0 + o * sp
    const ramp = (a: number, b: number) => clamp((T - a) / Math.max(0.001, b - a), 0, 1)
    return {
        genS: tween(T, 0.55, 1, at(0), at(0.6), easeOutBack) * (1 - 0.3 * ramp(at(4.2), at(4.8))),
        genOp: ramp(at(0), at(0.35)) * (1 - ramp(at(4.3), at(4.8))),
        settle: ramp(at(0.7), at(3.1)),
        clipS: tween(T, 0.4, 1, at(4.3), at(4.8), easeOutBack) * (1 - 0.25 * ramp(at(6.5), at(6.8))),
        clipOp: ramp(at(4.3), at(4.7)) * (1 - ramp(at(6.5), at(6.9))),
        carry: clamp((T - at(4.9)) / (1.5 * sp), 0, 1),
        pasteS: tween(T, 0.6, 1, at(6.6), at(7.1), easeOutBack) * (1 - 0.2 * ramp(at(8.2), at(8.6))),
        pasteOp: ramp(at(6.6), at(6.9)) * (1 - ramp(at(8.25), at(8.7))),
        ping: clamp((T - at(8.2)) / (0.9 * sp), 0, 1),
        pinging: T > at(8.2) && T < at(9.1),
        done: at(8.4),
    }
}

interface ActionSpec {
    node: 'O' | 'S'
    tone: 'reg' | 'vault'
    n: string
    key: string
    a: number
    b: number
}

/** What to click, in which app, when. Absolute seconds. */
const ACTIONS: ActionSpec[] = [
    { node: 'O', tone: 'reg', n: '1', key: 'action.createScheduler', a: REG + 0.3, b: REG + 2.4 },
    { node: 'O', tone: 'reg', n: '1', key: 'action.generate', a: REG + 2.4, b: REG + 7.0 },
    { node: 'S', tone: 'reg', n: '1', key: 'action.register', a: REG + 8.6, b: REG + 11.6 },
    { node: 'O', tone: 'vault', n: '2', key: 'action.vaults', a: CHO + 0.3, b: CHO + 2.2 },
    { node: 'O', tone: 'vault', n: '2', key: 'action.setupScheduler', a: CHO + 2.2, b: CHO + 4.0 },
    { node: 'O', tone: 'vault', n: '2', key: 'action.selectScheduler', a: CHO + 4.0, b: CHO + 5.4 },
    { node: 'O', tone: 'vault', n: '2', key: 'action.generate', a: CHO + 5.4, b: CHO + 10.4 },
    { node: 'S', tone: 'vault', n: '2', key: 'action.addVault', a: CHO + 11.6, b: SAV + 1.6 },
    { node: 'O', tone: 'vault', n: '2', key: 'action.setupScheduler', a: REP + 0.2, b: REP + 1.4 },
    { node: 'O', tone: 'vault', n: '2', key: 'action.generate', a: REP + 1.4, b: REP + 3.2 },
    { node: 'S', tone: 'vault', n: '2', key: 'action.addVault', a: REP + 3.4, b: REP + 5.4 },
    { node: 'O', tone: 'vault', n: '2', key: 'action.setupScheduler', a: REP + 5.8, b: REP + 7.0 },
    { node: 'O', tone: 'vault', n: '2', key: 'action.generate', a: REP + 7.0, b: REP + 8.8 },
    { node: 'S', tone: 'vault', n: '2', key: 'action.addVault', a: REP + 9.0, b: REP + 11.0 },
]

/* ============================================================================================ *
 * Pieces
 * ============================================================================================ */

type Tone = 'reg' | 'vault'
const toneVar = (tone: Tone) => (tone === 'reg' ? 'var(--reg)' : 'var(--vault)')
const toneBgVar = (tone: Tone) => (tone === 'reg' ? 'var(--reg-bg)' : 'var(--vault-bg)')

function Glow({ size, tone, op }: { size: number; tone: string; op: number }) {
    return (
        <div
            aria-hidden
            style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: size,
                height: size,
                marginLeft: -size / 2,
                marginTop: -size / 2,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${tone} 0%, transparent 68%)`,
                opacity: op,
            }}
        />
    )
}

interface ShellProps {
    cx: number
    cy: number
    size: number
    tone: string
    soft: string
    active: boolean
    op: number
    pop: number
    breathe: number
    label: string
    sub?: string
    subTone?: string
    ghost?: boolean
    /** Vault cards are a single card; the control-plane pair get the stacked-paper backing. */
    flat?: boolean
    L: Layout
    children: React.ReactNode
}

function Shell({
    cx,
    cy,
    size,
    tone,
    soft,
    active,
    op,
    pop,
    breathe,
    label,
    sub,
    subTone,
    ghost,
    flat,
    L,
    children,
}: ShellProps) {
    const lift = active ? 1 : 0
    return (
        <div style={{ position: 'absolute', left: cx - size / 2, top: cy - size / 2, width: size, opacity: op }}>
            <div
                className="vs-diagram-breathe"
                style={{
                    position: 'relative',
                    width: size,
                    height: size,
                    transform: `scale(${(1 + pop * 0.045) * (1 + breathe * 0.004)})`,
                }}
            >
                <Glow size={size * 2.1} tone={soft} op={active ? 0.95 : 0.25} />
                {flat ? null : (
                    <div
                        aria-hidden
                        style={{
                            position: 'absolute',
                            left: 16,
                            top: 22,
                            right: -16,
                            bottom: -22,
                            borderRadius: 30,
                            background: 'var(--stage)',
                            border: '1px solid var(--line)',
                            opacity: 0.5,
                        }}
                    />
                )}
                {flat ? null : (
                    <div
                        aria-hidden
                        style={{
                            position: 'absolute',
                            left: 8,
                            top: 10,
                            right: -8,
                            bottom: -10,
                            borderRadius: 30,
                            background: 'var(--face)',
                            border: '1px solid var(--line)',
                            opacity: 0.7,
                        }}
                    />
                )}
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: 30,
                        background: ghost ? 'transparent' : 'var(--face)',
                        border: `${active ? 2.5 : 1.5}px ${ghost ? 'dashed' : 'solid'} ${active ? tone : 'var(--line)'}`,
                        boxShadow: active ? 'var(--vs-diagram-shadow-2)' : 'var(--vs-diagram-shadow-1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        aria-hidden
                        style={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            top: 0,
                            height: '46%',
                            background: 'linear-gradient(180deg, var(--vs-diagram-gloss), transparent)',
                        }}
                    />
                    {children}
                </div>
                <div
                    aria-hidden
                    style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: -22 - lift * 4,
                        height: 16,
                        borderRadius: '50%',
                        background: 'hsl(0 0% 0% / 0.10)',
                        filter: 'blur(6px)',
                        opacity: ghost ? 0.3 : 0.55,
                    }}
                />
            </div>
            <div style={{ textAlign: 'center', marginTop: L.label.gap }}>
                <div
                    style={{
                        fontSize: L.label.size,
                        fontWeight: 600,
                        lineHeight: 1.2,
                        letterSpacing: '-0.012em',
                        color: ghost ? 'var(--ink-muted)' : 'var(--ink)',
                    }}
                >
                    {label}
                </div>
                {sub ? (
                    <div
                        style={{
                            fontSize: L.label.sub,
                            fontWeight: 500,
                            lineHeight: 1.3,
                            letterSpacing: '0.01em',
                            color: subTone || (active ? tone : 'var(--ink-muted)'),
                            marginTop: 6,
                        }}
                    >
                        {sub}
                    </div>
                ) : null}
            </div>
        </div>
    )
}

/** Orchestrator: a control grid with one lit cell — the centre, where the scheduler is created. */
function OrchCore({ hot, k }: { hot: number; k: number }) {
    return (
        <div
            aria-hidden
            style={{
                position: 'relative',
                width: 132,
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
                alignItems: 'center',
                transform: `scale(${k})`,
            }}
        >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 22px)', gap: 11 }}>
                {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                    <span
                        key={i}
                        style={{
                            width: 22,
                            height: 22,
                            borderRadius: 6,
                            background: i === 4 ? 'var(--reg)' : 'var(--stage)',
                            border: i === 4 ? 'none' : '1px solid var(--line)',
                            transform: `scale(${i === 4 ? 1 + hot * 0.18 : 1})`,
                        }}
                    />
                ))}
            </div>
            <div style={{ display: 'flex', gap: 7 }}>
                {[0, 1, 2].map((i) => (
                    <span
                        key={i}
                        style={{
                            width: i === 1 ? 34 : 18,
                            height: 7,
                            borderRadius: 4,
                            background: i === 1 ? 'var(--reg)' : 'var(--line)',
                            opacity: i === 1 ? 0.35 + hot * 0.65 : 0.7,
                        }}
                    />
                ))}
            </div>
        </div>
    )
}

/** Scheduler: four job rows. Inert until registered, then one row lights per beat. */
function SchedCore({ on, tick, k }: { on: boolean; tick: number; k: number }) {
    return (
        <div
            aria-hidden
            style={{ width: 136, display: 'flex', flexDirection: 'column', gap: 10, transform: `scale(${k})` }}
        >
            {[0, 1, 2, 3].map((i) => (
                <div
                    key={i}
                    style={{
                        height: 24,
                        borderRadius: 7,
                        background: 'var(--stage)',
                        border: `1px solid ${on ? 'var(--reg-soft)' : 'var(--line)'}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '0 9px',
                    }}
                >
                    <span
                        style={{
                            width: 8,
                            height: 8,
                            borderRadius: 999,
                            background: on ? 'var(--reg)' : 'var(--line)',
                            opacity: on ? (i === tick ? 1 : 0.45) : 0.8,
                        }}
                    />
                    <span
                        style={{
                            flex: 1,
                            height: 4,
                            borderRadius: 3,
                            background: on ? 'var(--reg-soft)' : 'var(--line)',
                            opacity: on ? 1 : 0.5,
                        }}
                    />
                </div>
            ))}
        </div>
    )
}

/** Vault: a door with a dial that turns once its route is open. */
function VaultCore({ on, spin, k }: { on: boolean; spin: number; k: number }) {
    const edge = on ? 'var(--vault)' : 'var(--line)'
    return (
        <div aria-hidden style={{ position: 'relative', width: 112, height: 100, transform: `scale(${k})` }}>
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 12,
                    background: on ? 'var(--vault-bg)' : 'var(--stage)',
                    border: `3px solid ${edge}`,
                    boxShadow: `inset 0 0 0 5px var(--face), inset 0 0 0 7px ${on ? 'var(--vault-soft)' : 'transparent'}`,
                }}
            />
            <div style={{ position: 'absolute', left: -7, top: 18, width: 10, height: 18, borderRadius: 3, background: edge }} />
            <div style={{ position: 'absolute', left: -7, bottom: 18, width: 10, height: 18, borderRadius: 3, background: edge }} />
            <div style={{ position: 'absolute', left: 30, top: 22, width: 56, height: 56 }}>
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '50%',
                        background: on ? 'var(--vault-bg)' : 'var(--stage)',
                        border: `3px solid ${edge}`,
                    }}
                />
                <div style={{ position: 'absolute', inset: 0, transform: `rotate(${spin}deg)` }}>
                    <span
                        style={{
                            position: 'absolute',
                            left: '50%',
                            top: 5,
                            width: 4,
                            height: 13,
                            marginLeft: -2,
                            borderRadius: 2,
                            background: edge,
                        }}
                    />
                    <span
                        style={{
                            position: 'absolute',
                            left: '50%',
                            bottom: 5,
                            width: 4,
                            height: 13,
                            marginLeft: -2,
                            borderRadius: 2,
                            background: edge,
                            opacity: 0.45,
                        }}
                    />
                </div>
                <span
                    style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        width: 16,
                        height: 16,
                        marginLeft: -8,
                        marginTop: -8,
                        borderRadius: '50%',
                        background: edge,
                    }}
                />
            </div>
            <div style={{ position: 'absolute', right: 9, top: 44, width: 20, height: 7, borderRadius: 4, background: edge }} />
            <span
                style={{
                    position: 'absolute',
                    left: 12,
                    top: 12,
                    width: 9,
                    height: 9,
                    borderRadius: 999,
                    background: edge,
                }}
            />
        </div>
    )
}

/**
 * The scheduler-to-vault fan. SVG, because these are pure geometry with no text — the one place
 * where SVG is the better tool.
 */
function Routes({ L, p, flow, op }: { L: Layout; p: (i: number) => number; flow: number; op: number }) {
    return (
        <svg
            aria-hidden
            width={L.W}
            height={L.H}
            viewBox={`0 0 ${L.W} ${L.H}`}
            style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none', opacity: op, overflow: 'visible' }}
        >
            {L.V.map((_, i) => {
                const a = L.routeStart
                const b = L.routeEnd(i)
                const t = clamp(p(i), 0, 1)
                if (t <= 0) return null
                const ex = a.x + (b.x - a.x) * t
                const ey = a.y + (b.y - a.y) * t
                return (
                    <g key={i}>
                        <line
                            x1={a.x}
                            y1={a.y}
                            x2={ex}
                            y2={ey}
                            stroke="var(--vault)"
                            strokeWidth={3}
                            strokeLinecap="round"
                        />
                        {t > 0.98
                            ? [0, 1, 2, 3].map((d) => {
                                  const f = (flow + d / 4) % 1
                                  return (
                                      <circle
                                          key={d}
                                          cx={a.x + (b.x - a.x) * f}
                                          cy={a.y + (b.y - a.y) * f}
                                          r={4.5}
                                          fill="var(--vault)"
                                          opacity={0.25 + 0.55 * Math.sin(f * Math.PI)}
                                      />
                                  )
                              })
                            : null}
                    </g>
                )
            })}
        </svg>
    )
}

interface TokenProps {
    x: number
    y: number
    op: number
    s: number
    kind: Tone
    title: string
    note: string
    payload: string
    settled: number
    frame: number
    L: Layout
}

/**
 * A token.
 *
 * The registration token is a PILL with a KEY glyph; the vault connection token is a
 * radius-12 RECTANGLE with a TAG glyph. Both spell out what they are. Do not collapse these into
 * one shape differentiated by colour — see the note at the top of this file.
 */
function Token({ x, y, op, s, kind, title, note, payload, settled, frame, L }: TokenProps) {
    const reg = kind === 'reg'
    const tone = toneVar(kind)
    const column = L.tokenAxis === 'column'
    if (op <= 0.001) return null
    return (
        <div
            style={{
                position: 'absolute',
                left: x,
                top: y,
                opacity: op,
                transform: `translate(-50%,-50%) scale(${s})`,
            }}
        >
            <div
                style={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: column ? 'column' : 'row',
                    alignItems: 'center',
                    gap: column ? 10 : 16,
                    background: 'var(--face)',
                    border: `2.5px solid ${tone}`,
                    borderRadius: reg ? 999 : 12,
                    padding: column ? '16px 22px' : reg ? '16px 26px' : '16px 22px',
                    boxShadow: 'var(--vs-diagram-shadow-2)',
                    whiteSpace: 'nowrap',
                }}
            >
                <span style={{ display: 'flex', alignItems: 'center', gap: column ? 12 : 0 }}>
                    {reg ? (
                        /* key: a ring with a bit welded to its right */
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ width: 26, height: 26, borderRadius: 999, border: `5px solid ${tone}` }} />
                            <span style={{ width: 20, height: 7, borderRadius: 2, background: tone, marginLeft: -3 }} />
                        </span>
                    ) : (
                        /* tag: a square with a solid core */
                        <span style={{ position: 'relative', width: 26, height: 26 }}>
                            <span style={{ position: 'absolute', inset: 0, borderRadius: 4, border: `3px solid ${tone}` }} />
                            <span
                                style={{
                                    position: 'absolute',
                                    left: '50%',
                                    top: '50%',
                                    width: 10,
                                    height: 10,
                                    marginLeft: -5,
                                    marginTop: -5,
                                    borderRadius: 2,
                                    background: tone,
                                }}
                            />
                        </span>
                    )}
                    {column ? (
                        <span
                            style={{
                                fontFamily: 'var(--diagram-mono)',
                                fontWeight: 600,
                                fontSize: 17,
                                letterSpacing: '0.05em',
                                color: tone,
                            }}
                        >
                            {title}
                        </span>
                    ) : null}
                </span>

                <span style={{ display: 'flex', flexDirection: 'column', gap: 7, alignItems: column ? 'center' : 'flex-start' }}>
                    {column ? null : (
                        <span
                            style={{
                                fontFamily: 'var(--diagram-mono)',
                                fontWeight: 600,
                                fontSize: 17,
                                letterSpacing: '0.05em',
                                color: tone,
                            }}
                        >
                            {title}
                        </span>
                    )}
                    <span
                        style={{
                            fontFamily: 'var(--diagram-mono)',
                            fontWeight: 500,
                            fontSize: 15,
                            letterSpacing: '0.02em',
                            color: settled >= 1 ? 'var(--ink)' : 'var(--ink-muted)',
                            background: 'var(--stage)',
                            border: '1px solid var(--line)',
                            borderRadius: 6,
                            padding: '6px 10px',
                        }}
                    >
                        {scrambleText(payload, settled, frame)}
                    </span>
                </span>

                <span
                    style={{
                        fontFamily: 'var(--diagram-mono)',
                        fontWeight: 500,
                        fontSize: 13,
                        color: 'var(--ink-muted)',
                        border: '1px solid var(--line)',
                        borderRadius: 999,
                        padding: '5px 11px',
                        background: 'var(--face)',
                    }}
                >
                    {note}
                </span>
            </div>
        </div>
    )
}

function Clipboard({
    x,
    y,
    op,
    s,
    tone,
    label,
    payload,
}: {
    x: number
    y: number
    op: number
    s: number
    tone: Tone
    label: string
    payload: string
}) {
    if (op <= 0.001) return null
    const c = toneVar(tone)
    return (
        <div
            style={{
                position: 'absolute',
                left: x,
                top: y,
                opacity: op,
                transform: `translate(-50%,-50%) scale(${s})`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 10,
            }}
        >
            <div aria-hidden style={{ position: 'relative', width: 54, height: 66 }}>
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: 9,
                        background: 'var(--face)',
                        border: `2px solid ${c}`,
                        boxShadow: 'var(--vs-diagram-shadow-1)',
                    }}
                />
                <div
                    style={{ position: 'absolute', left: 13, top: -7, width: 28, height: 14, borderRadius: 5, background: c }}
                />
                <div style={{ position: 'absolute', left: 12, top: 24, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {[22, 30, 16].map((w, i) => (
                        <span key={i} style={{ width: w, height: 4, borderRadius: 3, background: c, opacity: 0.4 }} />
                    ))}
                </div>
            </div>
            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <span
                    style={{
                        fontFamily: 'var(--diagram-mono)',
                        fontWeight: 500,
                        fontSize: 14,
                        color: c,
                        background: 'var(--face)',
                        border: '1px solid var(--line)',
                        borderRadius: 999,
                        padding: '6px 13px',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {label}
                </span>
                <span
                    style={{
                        fontFamily: 'var(--diagram-mono)',
                        fontWeight: 400,
                        fontSize: 12,
                        color: 'var(--ink-muted)',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {payload.slice(0, 12) + '…'}
                </span>
            </span>
        </div>
    )
}

function ActionPill({ x, y, text, tone, op, n }: { x: number; y: number; text: string; tone: Tone; op: number; n: string }) {
    if (op <= 0.001) return null
    const c = toneVar(tone)
    return (
        <div
            style={{
                position: 'absolute',
                left: x,
                top: y,
                transform: 'translateX(-50%)',
                opacity: op,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: 'var(--face)',
                border: `1.5px solid ${c}`,
                borderRadius: 999,
                padding: '10px 18px',
                boxShadow: 'var(--vs-diagram-shadow-1)',
                whiteSpace: 'nowrap',
            }}
        >
            <span
                style={{
                    width: 22,
                    height: 22,
                    borderRadius: 999,
                    background: c,
                    color: 'var(--tone-ink)',
                    fontFamily: 'var(--diagram-mono)',
                    fontWeight: 600,
                    fontSize: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {n}
            </span>
            <span style={{ fontSize: 17, fontWeight: 500, color: 'var(--ink)' }}>{text}</span>
        </div>
    )
}

function Confirm({ x, y, text, op, pop }: { x: number; y: number; text: string; op: number; pop: number }) {
    if (op <= 0.001) return null
    return (
        <div
            style={{
                position: 'absolute',
                left: x,
                top: y,
                transform: `translateX(-50%) scale(${1 + pop * 0.06})`,
                opacity: op,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                background: 'var(--ok-bg)',
                border: '2px solid var(--ok)',
                borderRadius: 999,
                padding: '12px 22px',
                boxShadow: 'var(--vs-diagram-shadow-1)',
                whiteSpace: 'nowrap',
            }}
        >
            <span
                aria-hidden
                style={{
                    width: 24,
                    height: 24,
                    borderRadius: 999,
                    background: 'var(--ok)',
                    color: 'var(--tone-ink)',
                    fontSize: 14,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                ✓
            </span>
            <span style={{ fontSize: 18, fontWeight: 600, color: 'var(--ok)' }}>{text}</span>
        </div>
    )
}

function Ping({ x, y, p, tone }: { x: number; y: number; p: number; tone: Tone }) {
    return (
        <div aria-hidden style={{ position: 'absolute', left: x, top: y, width: 1, height: 1 }}>
            <span
                style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: 160,
                    height: 160,
                    marginLeft: -80,
                    marginTop: -80,
                    borderRadius: '50%',
                    border: `3px solid ${toneVar(tone)}`,
                    opacity: (1 - p) * 0.8,
                    transform: `scale(${0.3 + p * 1.5})`,
                }}
            />
        </div>
    )
}

function StepChip({
    x,
    y,
    n,
    tone,
    title,
    sub,
    op,
}: {
    x: number
    y: number
    n: string
    tone: Tone
    title: string
    sub: string
    op: number
}) {
    if (op <= 0.001) return null
    return (
        <div
            style={{
                position: 'absolute',
                left: x,
                top: y,
                transform: 'translateX(-50%)',
                textAlign: 'center',
                opacity: op,
                whiteSpace: 'nowrap',
            }}
        >
            <div
                style={{
                    width: 38,
                    height: 38,
                    borderRadius: 999,
                    background: toneVar(tone),
                    color: 'var(--tone-ink)',
                    fontFamily: 'var(--diagram-mono)',
                    fontWeight: 600,
                    fontSize: 18,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 12px',
                }}
            >
                {n}
            </div>
            <div style={{ fontSize: 21, fontWeight: 600, color: 'var(--ink)' }}>{title}</div>
            <div style={{ fontSize: 15, fontWeight: 500, letterSpacing: '0.01em', color: 'var(--ink-muted)', marginTop: 5 }}>
                {sub}
            </div>
        </div>
    )
}

/** Transparent click/keyboard target over a node. Only rendered when there is somewhere to go. */
function Hotspot({
    x,
    y,
    s,
    extra,
    label,
    onActivate,
}: {
    x: number
    y: number
    s: number
    extra: number
    label: string
    onActivate: () => void
}) {
    return (
        <div
            className="vs-diagram-hotspot"
            role="button"
            tabIndex={0}
            aria-label={label}
            title={label}
            onClick={onActivate}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    onActivate()
                }
            }}
            style={{
                position: 'absolute',
                left: x - s / 2 - 10,
                top: y - s / 2 - 10,
                width: s + 20,
                height: s + extra,
                borderRadius: 34,
                cursor: 'pointer',
                outline: 'none',
            }}
        />
    )
}

/* ============================================================================================ *
 * The scene — one pure render of the whole diagram at time T
 * ============================================================================================ */

export type DiagramNav = 'orchestrator' | 'scheduler' | 'vault'

interface SceneProps {
    T: number
    L: Layout
    tr: (key: string) => string
    onNavigate?: (dest: DiagramNav, index?: number) => void
    captionLive: boolean
}

function Scene({ T, L, tr, onNavigate, captionLive }: SceneProps) {
    const ramp = (a: number, b: number) => clamp((T - a) / Math.max(0.001, b - a), 0, 1)
    /** A 0.35s fade-in / fade-out envelope. Exactly 0 at both ends. */
    const win = (a: number, b: number) => ramp(a, a + 0.35) * (1 - ramp(b - 0.35, b))
    /** A 0.6s 0 -> 1 -> 0 pulse that settles back to 0 and stays there. */
    const bump = (at: number) => (T > at ? Math.sin(clamp((T - at) / 0.6, 0, 1) * Math.PI) : 0)

    const outAll = 1 - ramp(RCP + 4.0, RCP + 4.7)
    const breathe = Math.sin(T * 1.15)
    const flow = (T * 0.24) % 1
    const frame = Math.floor(T * 20)
    const tick = Math.floor(T * 2.4) % 4

    const reg = hopSeq(T, REG + 2.6, 1)
    const vt = [hopSeq(T, CHO + 5.4, 1), hopSeq(T, REP + 0.4, 0.55), hopSeq(T, REP + 6.0, 0.55)]

    const registered = T > reg.done
    const vaultDone = [vt[0].done, vt[1].done, vt[2].done]
    const vaultLink = (i: number) => ramp(vaultDone[i] + 0.1, vaultDone[i] + 1.3)
    const vaultOn = (i: number) => T > vaultDone[i] + 1.0
    const focus = (i: number) => clamp(1 - Math.abs([CHO + 3.0, REP + 1.0, REP + 6.6][i] - T) / 3.2, 0, 1)
    const orchHot = clamp(bump(REG + 3.0) + bump(CHO + 5.8) + bump(REP + 0.8) + bump(REP + 6.4), 0, 1)

    const phaseKey = T < CHO - 0.2 ? 'phase.scheduler' : T < RCP ? 'phase.vaults' : 'phase.recap'
    const phaseTone = T < CHO - 0.2 ? 'var(--reg)' : T < RCP ? 'var(--vault)' : 'var(--ink-muted)'

    // Caption: the last item whose `at` has passed, crossfading with its neighbours.
    let capIdx = -1
    for (let i = 0; i < CAPTIONS.length; i++) if (CAPTIONS[i].at <= T) capIdx = i
    let capText = ''
    let capOp = 0
    if (capIdx >= 0) {
        const cur = CAPTIONS[capIdx]
        const end = cur.until ?? (capIdx + 1 < CAPTIONS.length ? CAPTIONS[capIdx + 1].at : Infinity)
        if (T < end) {
            capText = tr(cur.key)
            capOp = clamp(Math.min((T - cur.at) / CAPTION_FADE, (end - T) / CAPTION_FADE), 0, 1)
        }
    }

    const node = (which: 'O' | 'S') => (which === 'O' ? L.O : L.S)

    return (
        <>
            {/* Stage wash: a drifting dot grid and a vignette. Decorative, no information. */}
            <div
                aria-hidden
                className="vs-diagram-grid"
                style={{
                    position: 'absolute',
                    inset: -40,
                    backgroundImage: 'radial-gradient(var(--vs-diagram-grid) 1.4px, transparent 1.4px)',
                    backgroundSize: '38px 38px',
                    transform: `translate(${(T * 2) % 38}px, ${-(T * 1.2) % 38}px)`,
                    opacity: 0.55,
                }}
            />

            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    transformOrigin: '0 0',
                    transform: `translate(${L.offsetX}px, ${L.offsetY}px)`,
                }}
            >
                <Routes L={L} p={vaultLink} flow={flow} op={outAll} />

                <Shell
                    L={L}
                    cx={L.O.x}
                    cy={L.O.y}
                    size={L.O.s}
                    tone="var(--reg)"
                    soft="var(--reg-soft)"
                    active={orchHot > 0.05}
                    op={ramp(0, 0.8) * outAll}
                    pop={bump(REG + 3.0) + bump(CHO + 5.8)}
                    breathe={breathe}
                    label={tr('node.orchestrator')}
                    sub={tr('node.orchestrator.sub')}
                >
                    <OrchCore hot={orchHot} k={L.O.s / WIDE.O.s} />
                </Shell>

                <Shell
                    L={L}
                    cx={L.S.x}
                    cy={L.S.y}
                    size={L.S.s}
                    tone="var(--reg)"
                    soft="var(--reg-soft)"
                    active={registered}
                    op={ramp(0.3, 1.1) * outAll}
                    pop={bump(reg.done)}
                    breathe={-breathe}
                    label={tr('node.scheduler')}
                    sub={registered ? tr('state.active') : tr('state.pending')}
                    subTone={registered ? 'var(--ok)' : undefined}
                >
                    <SchedCore on={registered} tick={tick} k={L.S.s / WIDE.S.s} />
                </Shell>

                {L.V.map((v, i) => (
                    <Shell
                        key={i}
                        L={L}
                        flat
                        cx={v.x}
                        cy={v.y}
                        size={v.s}
                        tone="var(--vault)"
                        soft="var(--vault-soft)"
                        active={vaultOn(i)}
                        op={
                            (0.6 + 0.4 * Math.max(focus(i), vaultOn(i) ? 1 : 0)) *
                            ramp(0.6 + i * 0.18, 1.4 + i * 0.18) *
                            outAll
                        }
                        pop={bump(vaultDone[i] + 1.0)}
                        breathe={i % 2 ? breathe : -breathe}
                        label={`${tr('node.vault')} ${i + 1}`}
                    >
                        <VaultCore
                            on={vaultOn(i)}
                            spin={vaultOn(i) ? 128 + Math.sin(T * 0.5 + i) * 7 : 0}
                            k={v.s / WIDE.V[0].s}
                        />
                    </Shell>
                ))}

                {/* One scheduler is never the whole story. */}
                <Shell
                    L={L}
                    ghost
                    cx={L.ghost.x}
                    cy={L.ghost.y}
                    size={L.ghost.s}
                    tone="var(--ink-muted)"
                    soft="transparent"
                    active={false}
                    op={ramp(RCP + 2.0, RCP + 2.7) * 0.7 * outAll}
                    pop={0}
                    breathe={breathe}
                    label={tr('node.scheduler.more')}
                >
                    <SchedCore on={false} tick={-1} k={L.ghost.s / WIDE.S.s} />
                </Shell>

                {/* Step 1 — the registration hop. */}
                <Token
                    L={L}
                    x={L.origin.x}
                    y={L.origin.y}
                    op={reg.genOp * outAll}
                    s={reg.genS}
                    kind="reg"
                    title={tr('token.registration')}
                    note={tr('token.once')}
                    payload={PAYLOAD_REG}
                    settled={reg.settle}
                    frame={frame}
                />
                <Clipboard
                    x={L.carry(reg.carry).x}
                    y={L.carry(reg.carry).y}
                    op={reg.clipOp * outAll}
                    s={reg.clipS}
                    tone="reg"
                    label={tr('clip.copied')}
                    payload={PAYLOAD_REG}
                />
                <Token
                    L={L}
                    x={L.dest.x}
                    y={L.dest.y}
                    op={reg.pasteOp * outAll}
                    s={reg.pasteS}
                    kind="reg"
                    title={tr('token.registration')}
                    note={tr('action.paste')}
                    payload={PAYLOAD_REG}
                    settled={1}
                    frame={frame}
                />
                {reg.pinging ? <Ping x={L.S.x} y={L.S.y} p={reg.ping} tone="reg" /> : null}

                {/* Step 2 — one hop per vault. */}
                {vt.map((v, i) => (
                    <div key={i}>
                        <Token
                            L={L}
                            x={L.origin.x}
                            y={L.origin.y}
                            op={v.genOp * outAll}
                            s={v.genS}
                            kind="vault"
                            title={tr('token.vault')}
                            note={`${i + 1} / 3`}
                            payload={PAYLOAD_VAULT[i]}
                            settled={v.settle}
                            frame={frame}
                        />
                        <Clipboard
                            x={L.carry(v.carry).x}
                            y={L.carry(v.carry).y}
                            op={v.clipOp * outAll}
                            s={v.clipS}
                            tone="vault"
                            label={tr('clip.copied')}
                            payload={PAYLOAD_VAULT[i]}
                        />
                        <Token
                            L={L}
                            x={L.dest.x}
                            y={L.dest.y}
                            op={v.pasteOp * outAll}
                            s={v.pasteS}
                            kind="vault"
                            title={tr('token.vault')}
                            note={tr('action.paste')}
                            payload={PAYLOAD_VAULT[i]}
                            settled={1}
                            frame={frame}
                        />
                        {v.pinging ? <Ping x={L.S.x} y={L.S.y} p={v.ping} tone="vault" /> : null}
                    </div>
                ))}

                {ACTIONS.map((a, i) => (
                    <ActionPill
                        key={i}
                        x={node(a.node).x}
                        y={node(a.node).y + L.actionDY}
                        tone={a.tone}
                        n={a.n}
                        text={tr(a.key)}
                        op={win(a.a, a.b) * outAll}
                    />
                ))}

                <Confirm
                    x={L.S.x}
                    y={L.S.y + L.confirmSchedDY}
                    text={tr('ok.registered')}
                    op={ramp(reg.done + 0.2, reg.done + 0.7) * (1 - ramp(CHO - 0.6, CHO - 0.1)) * outAll}
                    pop={bump(reg.done + 0.2)}
                />
                {L.V.map((_, i) => (
                    <Confirm
                        key={i}
                        x={L.vaultConfirm(i).x}
                        y={L.vaultConfirm(i).y}
                        text={`${tr('node.vault')} ${i + 1} ${tr('ok.vault')}`}
                        op={ramp(vaultDone[i] + 1.1, vaultDone[i] + 1.6) * outAll}
                        pop={bump(vaultDone[i] + 1.1)}
                    />
                ))}

                {/* The gate: only a registered scheduler can be selected. */}
                {win(CHO + 4.0, CHO + 5.6) * outAll > 0.001 ? (
                    <div
                        style={{
                            position: 'absolute',
                            left: L.gate.x,
                            top: L.gate.y,
                            transform: 'translateX(-50%)',
                            opacity: win(CHO + 4.0, CHO + 5.6) * outAll,
                            whiteSpace: 'nowrap',
                            background: toneBgVar('reg'),
                            border: '1px solid var(--reg)',
                            borderRadius: 999,
                            padding: '11px 22px',
                            fontSize: 18,
                            fontWeight: 500,
                            color: 'var(--reg)',
                        }}
                    >
                        {tr('gate.short')}
                    </div>
                ) : null}

                {ramp(RCP + 0.2, RCP + 0.8) * outAll > 0.001 ? (
                    <div
                        style={{
                            position: 'absolute',
                            left: L.count.x,
                            top: L.count.y,
                            transform: 'translateX(-50%)',
                            fontFamily: 'var(--diagram-mono)',
                            fontSize: 18,
                            fontWeight: 500,
                            color: 'var(--vault)',
                            opacity: ramp(RCP + 0.2, RCP + 0.8) * outAll,
                            whiteSpace: 'nowrap',
                            background: toneBgVar('vault'),
                            border: '1px solid var(--vault)',
                            borderRadius: 999,
                            padding: '10px 20px',
                        }}
                    >
                        {tr('count.vaults')}
                    </div>
                ) : null}

                <StepChip
                    x={L.chip1.x}
                    y={L.chip1.y}
                    n="1"
                    tone="reg"
                    title={tr('step.1.title')}
                    sub={tr('step.1.sub')}
                    op={ramp(RCP + 0.3, RCP + 1.0) * outAll}
                />
                <StepChip
                    x={L.chip2.x}
                    y={L.chip2.y}
                    n="2"
                    tone="vault"
                    title={tr('step.2.title')}
                    sub={tr('step.2.sub')}
                    op={ramp(RCP + 1.1, RCP + 1.8) * outAll}
                />

                {onNavigate ? (
                    <>
                        <Hotspot
                            x={L.O.x}
                            y={L.O.y}
                            s={L.O.s}
                            extra={L.hotspotExtra}
                            label={tr('nav.orch')}
                            onActivate={() => onNavigate('orchestrator')}
                        />
                        <Hotspot
                            x={L.S.x}
                            y={L.S.y}
                            s={L.S.s}
                            extra={L.hotspotExtra}
                            label={tr('nav.sched')}
                            onActivate={() => onNavigate('scheduler')}
                        />
                        {L.V.map((v, i) => (
                            <Hotspot
                                key={i}
                                x={v.x}
                                y={v.y}
                                s={v.s}
                                extra={L.hotspotExtra}
                                label={`${tr('node.vault')} ${i + 1} — ${tr('nav.vault')}`}
                                onActivate={() => onNavigate('vault', i)}
                            />
                        ))}
                    </>
                ) : null}
            </div>

            {/* Heading and caption live OUTSIDE the offset group, in raw canvas space. */}
            <div style={{ position: 'absolute', left: L.heading.x, top: L.heading.y }}>
                <div style={{ fontSize: L.heading.title, fontWeight: 600, letterSpacing: '-0.022em', lineHeight: 1.1 }}>
                    {tr('diagram.title')}
                </div>
                <div style={{ fontSize: L.heading.phase, fontWeight: 500, color: phaseTone, marginTop: 12 }}>
                    {tr(phaseKey)}
                </div>
            </div>

            <div
                aria-live={captionLive ? 'polite' : 'off'}
                aria-atomic="true"
                style={{
                    position: 'absolute',
                    left: L.caption.x,
                    top: L.caption.y,
                    maxWidth: L.caption.maxW,
                    display: 'inline-block',
                    textAlign: 'left',
                    fontSize: L.caption.size,
                    fontWeight: 500,
                    lineHeight: 1.45,
                    color: 'var(--ink)',
                    background: 'var(--face)',
                    border: '1px solid var(--line)',
                    borderRadius: 16,
                    padding: '14px 24px',
                    boxShadow: 'var(--vs-diagram-shadow-1)',
                    opacity: capOp,
                    pointerEvents: 'none',
                }}
            >
                {capText}
            </div>
        </>
    )
}

/* ============================================================================================ *
 * The component
 * ============================================================================================ */

export interface SchedulerConnectionDiagramProps {
    /**
     * Open at one of the twelve step points (1-12). Supports the ?step=N deep link from docs and
     * support: the host reads the query string and passes it down. When omitted, the component
     * falls back to reading ?step=N off window.location itself, so the deep link works without a
     * router dependency (this renders inside a modal, not a route).
     */
    initialStep?: number
    /** Open at the beginning and play, rather than frozen on the recap. The modal sets this. */
    autoStart?: boolean
    /**
     * Called when a node is activated. Node hotspots only exist when this is supplied — a
     * focusable control that navigates nowhere is worse than no control. Omit it on a surface
     * that is already the destination.
     */
    onNavigate?: (dest: DiagramNav, index?: number) => void
    className?: string
}

export function SchedulerConnectionDiagram({ initialStep, autoStart, onNavigate, className }: SchedulerConnectionDiagramProps) {
    const { t } = useTranslation('schedulerDiagram')
    const tr = useCallback((key: string): string => String(t(key)), [t])

    const reducedMotion = usePrefersReducedMotion()
    const clock = useDiagramClock(DURATION, true)
    const { t: T, playing, play, pause, seekTo, playUntil } = clock

    // prefers-reduced-motion pins the diagram to step mode permanently. There is no choice to
    // offer, so the Auto/Step toggle is not rendered at all in that case.
    const [autoMode, setAutoMode] = useState(true)
    const mode: 'auto' | 'step' = reducedMotion ? 'step' : autoMode ? 'auto' : 'step'

    /** True until the user does anything. Until then the diagram is a frozen recap tableau. */
    const [idle, setIdle] = useState(true)

    const wrapRef = useRef<HTMLDivElement | null>(null)
    const [width, setWidth] = useState(0)

    const L = width > 0 && width < NARROW_BREAKPOINT ? NARROW : WIDE
    const scale = width > 0 ? width / L.W : 1

    /**
     * Measure from THREE sources, following GlobalMap's precedent: ResizeObserver has been seen
     * on this codebase's rigs to never fire even its initial callback, and a canvas frozen at
     * width 0 is invisible rather than merely wrong.
     */
    useLayoutEffect(() => {
        const el = wrapRef.current
        if (!el) return
        const measure = () => {
            const w = el.clientWidth
            if (w > 0) setWidth(w)
        }
        let ro: ResizeObserver | null = null
        try {
            ro = new ResizeObserver(measure)
            ro.observe(el)
        } catch {
            /* no RO: the window listener and the mount call still cover it */
        }
        window.addEventListener('resize', measure)
        measure()
        return () => {
            ro?.disconnect()
            window.removeEventListener('resize', measure)
        }
    }, [])

    /**
     * Where the diagram opens.
     *
     * A deep-linked step wins. Otherwise `autoStart` decides, and the two cases are genuinely
     * different: embedded in a page, motion nobody asked for is an ambush, so the diagram sits
     * frozen on the recap — the finished picture, which is at least meaningful at a glance —
     * with a play affordance. Opened from a button that says "Show Me", the click IS the
     * request, and starting on the last frame answers a question the user did not ask. So that
     * case begins at the beginning and plays.
     *
     * Reduced motion still never animates: it lands on step 1 in step-by-step mode instead.
     */
    useEffect(() => {
        let step = initialStep
        if (step == null && typeof window !== 'undefined') {
            const raw = new URLSearchParams(window.location.search).get('step')
            const parsed = raw == null ? NaN : Number(raw)
            if (Number.isInteger(parsed) && parsed >= 1 && parsed <= BEATS.length) step = parsed
        }
        if (step != null && step >= 1 && step <= BEATS.length) {
            seekTo(BEATS[step - 1])
            setIdle(false)
        } else if (autoStart) {
            seekTo(0)
            setIdle(false)
            if (!reducedMotion) play()
        } else {
            seekTo(RECAP_T)
        }
        // Mount only: re-seeking on every prop identity change would fight the user's transport.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const stepIndex = useMemo(() => BEATS.filter((b) => b <= T + 0.1).length, [T])

    const goNext = useCallback(() => {
        setIdle(false)
        const next = BEATS.find((b) => b > T + 0.1)
        if (next == null) {
            // Past the last beat: wrap to the start and run to beat 2, as the prototype does.
            seekTo(0.01)
            playUntil(BEATS[1])
            return
        }
        if (reducedMotion) seekTo(next)
        else playUntil(next)
    }, [T, seekTo, playUntil, reducedMotion])

    const goBack = useCallback(() => {
        setIdle(false)
        // Back jumps instantly. There is no reverse playback.
        const prev = [...BEATS].reverse().find((b) => b < T - 0.3)
        seekTo(prev ?? 0)
    }, [T, seekTo])

    const togglePlay = useCallback(() => {
        setIdle(false)
        if (playing) pause()
        else play()
    }, [playing, play, pause])

    const switchMode = useCallback(
        (next: 'auto' | 'step') => {
            setIdle(false)
            setAutoMode(next === 'auto')
            if (next === 'auto') play()
            else pause()
        },
        [play, pause]
    )

    const startFromTop = useCallback(() => {
        setIdle(false)
        if (mode === 'step') {
            seekTo(BEATS[0])
        } else {
            seekTo(0)
            play()
        }
    }, [mode, seekTo, play])

    /** Arrow keys = Back/Next, Space = play/pause. Scoped to the diagram, never to the window. */
    const onKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            const target = e.target as HTMLElement | null
            const tag = target?.tagName
            const interactive = tag === 'BUTTON' || tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA'
            if (e.key === 'ArrowRight') {
                e.preventDefault()
                goNext()
            } else if (e.key === 'ArrowLeft') {
                e.preventDefault()
                goBack()
            } else if (e.key === ' ' && !interactive) {
                // Space on a focused button is that button's own activation, not ours.
                e.preventDefault()
                togglePlay()
            }
        },
        [goNext, goBack, togglePlay]
    )

    const btn =
        'inline-flex items-center justify-center rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium ' +
        'text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 ' +
        'focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'

    const segment = (on: boolean) =>
        'px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 ' +
        'focus-visible:ring-ring focus-visible:ring-inset ' +
        (on ? 'bg-primary text-primary-foreground' : 'bg-card text-foreground hover:bg-accent')

    return (
        <div
            className={className}
            role="group"
            aria-label={tr('diagram.title')}
            onKeyDown={onKeyDown}
            style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
            <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
                <div
                    className="vs-diagram"
                    style={{ position: 'relative', width: '100%', height: L.H * scale, borderRadius: 12 }}
                >
                    <div
                        className="vs-diagram-canvas"
                        style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            width: L.W,
                            height: L.H,
                            transform: `scale(${scale})`,
                        }}
                    >
                        <Scene T={T} L={L} tr={tr} onNavigate={onNavigate} captionLive={mode === 'step'} />
                    </div>
                </div>

                {/* Play affordance over the frozen recap. Pointer-events are off everywhere except
                    the button itself, so the node hotspots underneath stay reachable. */}
                {idle ? (
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                        }}
                    >
                        <button
                            type="button"
                            onClick={startFromTop}
                            className="pointer-events-auto inline-flex items-center gap-3 rounded-full border-2 border-primary bg-card px-6 py-3 text-base font-semibold text-foreground shadow-lg transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        >
                            <span aria-hidden className="text-primary">
                                ▶
                            </span>
                            {mode === 'step' ? tr('ui.next') : tr('ui.play')}
                        </button>
                    </div>
                ) : null}
            </div>

            {/* The only toolbar. One row: mode, then that mode's transport. */}
            <div className="flex flex-wrap items-center gap-2">
                {reducedMotion ? null : (
                    <div className="inline-flex overflow-hidden rounded-full border border-border" role="group">
                        <button
                            type="button"
                            className={segment(mode === 'auto')}
                            aria-pressed={mode === 'auto'}
                            onClick={() => switchMode('auto')}
                        >
                            {tr('ui.mode.auto')}
                        </button>
                        <button
                            type="button"
                            className={segment(mode === 'step')}
                            aria-pressed={mode === 'step'}
                            onClick={() => switchMode('step')}
                        >
                            {tr('ui.mode.step')}
                        </button>
                    </div>
                )}

                {mode === 'auto' ? (
                    <button type="button" className={btn} onClick={togglePlay} aria-label={playing ? tr('ui.pause') : tr('ui.play')}>
                        <span aria-hidden>{playing ? '❚❚' : '▶'}</span>
                        <span className="ml-2">{playing ? tr('ui.pause') : tr('ui.play')}</span>
                    </button>
                ) : (
                    <>
                        <button type="button" className={btn} onClick={goBack}>
                            <span aria-hidden className="mr-2">
                                ◀
                            </span>
                            {tr('ui.back')}
                        </button>
                        <button type="button" className={btn} onClick={goNext}>
                            {tr('ui.next')}
                            <span aria-hidden className="ml-2">
                                ▶
                            </span>
                        </button>
                        <span className="font-mono text-xs text-muted-foreground" aria-live="polite">
                            {stepIndex} / {BEATS.length}
                        </span>
                    </>
                )}
            </div>
        </div>
    )
}

export default SchedulerConnectionDiagram
